/*
 * Seletor de combo (kit) da página de produto.
 *
 * Cada card representa um valor da opção de combo do produto. Clicar no card
 * marca o input nativo daquela opção e dispara `change`, então quem atualiza
 * preço, galeria, estoque e o botão de compra continua sendo o
 * <variant-radios>/<variant-selects> do tema.
 *
 * Os valores dos cards dependem das demais opções (sabor, tamanho...), por isso
 * são recalculados a cada troca de variante.
 */
class KitSelector extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    const configEl = this.querySelector('[data-kit-selector-config]');
    if (!configEl) return;

    try {
      this.config = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }

    this.initialized = true;
    this.items = Array.from(this.querySelectorAll('[data-kit-item]'));
    this.productForm = document.getElementById('product-form-' + this.config.productId);

    /* O tema reusa o mesmo id no seletor da barra fixa de compra, então pode
       existir mais de um #product-option-<id> na página. Fica com o da coluna
       principal; o da barra fixa só serve de reserva. */
    const pickers = Array.from(document.querySelectorAll('[id="product-option-' + this.config.productId + '"]'));
    this.picker =
      pickers.find((el) => !el.querySelector('variant-sticky-radios, variant-sticky-selects')) || pickers[0] || null;

    this.items.forEach((item) => {
      const input = item.querySelector('.kit-card__input');
      if (!input) return;
      input.addEventListener('change', () => this.onCardSelect(item));
    });

    this.onExternalChange = this.onExternalChange.bind(this);
    document.addEventListener('change', this.onExternalChange);

    this.refresh();
    this.preselectHighlighted();
  }

  /* O card em destaque já nasce escolhido. Não mexemos quando o cliente chegou
     por um link com ?variant= — nesse caso a escolha dele vale mais. */
  preselectHighlighted() {
    if (new URLSearchParams(window.location.search).has('variant')) return;

    const target = this.items.find((item) => item.hasAttribute('data-kit-highlighted'));
    if (!target || target.hidden) return;
    if (target.classList.contains('is-selected') || target.classList.contains('is-unavailable')) return;

    this.onCardSelect(target);
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onExternalChange);
  }

  /* ---------- helpers ---------- */

  get variantIdInput() {
    return this.productForm ? this.productForm.querySelector('input[name="id"]') : null;
  }

  currentVariant() {
    const input = this.variantIdInput;
    const id = input ? parseInt(input.value, 10) : NaN;
    const found = this.config.variants.find((v) => v.id === id);
    return found || this.config.variants.find((v) => v.available) || this.config.variants[0];
  }

  /* Variante equivalente ao card, mantendo as demais opções como estão. */
  variantForItem(item, current) {
    const value = item.dataset.kitValue;
    const index = this.config.optionIndex;

    return this.config.variants.find((variant) =>
      variant.options.every((option, i) => (i === index ? option === value : option === current.options[i]))
    );
  }

  money(cents) {
    const value = Math.round(cents);
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(value, this.config.moneyFormat);
    }
    const parts = (value / 100).toFixed(2).split('.');
    return 'R$ ' + parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1];
  }

  installments(cents) {
    let count = Math.floor(cents / this.config.parcelaMin);
    if (count < 1) count = 1;
    if (count > this.config.parcelasMax) count = this.config.parcelasMax;
    return { count: count, value: cents / count };
  }

  /* ---------- eventos ---------- */

  onCardSelect(item) {
    if (item.classList.contains('is-unavailable')) return;

    const current = this.currentVariant();
    const variant = this.variantForItem(item, current);
    const applied = this.applyToNativePicker(item.dataset.kitValue);

    this.markSelected(item);

    if (!applied) {
      /* Sem picker nativo na página: ao menos o formulário aponta para a
         variante certa, mesmo que o preço do tema não seja re-renderizado. */
      const input = this.variantIdInput;
      if (variant && input) {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.refresh();
      return;
    }

    if (variant) this.ensureFormVariant(variant);
  }

  /* Rede de segurança: se o bloco "Seletor de Variante" estiver desativado, o
     único picker da página é o da barra fixa, que atualiza só o formulário dela.
     Aqui conferimos se o formulário principal realmente apontou para a variante
     do card — sem isso o cliente poderia levar a variante errada pro carrinho. */
  ensureFormVariant(variant) {
    window.setTimeout(() => {
      const input = this.variantIdInput;
      if (!input || parseInt(input.value, 10) === variant.id) return;
      input.value = variant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this.refresh();
    }, 150);
  }

  /* Repassa a escolha para o input nativo da opção de combo. */
  applyToNativePicker(value) {
    if (!this.picker || this.config.optionIndex < 0) return false;

    const radios = Array.from(
      this.picker.querySelectorAll('fieldset[data-option-index="' + this.config.optionIndex + '"] input[type="radio"]')
    );
    const radio = radios.find((input) => input.value === value);
    if (radio) {
      if (radio.checked) return true;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    const select = this.picker.querySelectorAll('select')[this.config.optionIndex];
    if (select) {
      if (select.value === value) return true;
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    return false;
  }

  onExternalChange(event) {
    const target = event.target;
    if (!target || !target.matches) return;

    const isVariantInput = target.matches('input[name="id"]') && this.productForm && this.productForm.contains(target);
    const isPickerInput = this.picker && this.picker.contains(target);

    if (isVariantInput || isPickerInput) this.refresh();
  }

  /* ---------- render ---------- */

  markSelected(selectedItem) {
    this.items.forEach((item) => {
      const isSelected = item === selectedItem;
      item.classList.toggle('is-selected', isSelected);
      const input = item.querySelector('.kit-card__input');
      if (input) input.checked = isSelected;
    });
  }

  refresh() {
    const current = this.currentVariant();
    if (!current) return null;

    let selectedItem = null;

    /* Primeira passada: resolve a variante de cada card para a seleção atual. */
    const rows = [];
    this.items.forEach((item) => {
      const qty = parseInt(item.dataset.kitQty, 10) || 0;
      const variant = this.variantForItem(item, current);

      if (!variant) {
        item.hidden = true;
        return;
      }
      item.hidden = false;
      item.dataset.kitVariant = variant.id;

      item.classList.toggle('is-unavailable', !variant.available);
      const input = item.querySelector('.kit-card__input');
      if (input) input.disabled = !variant.available;

      rows.push({ item: item, qty: qty, variant: variant });
    });

    /* Referência de economia: o valor unitário do menor combo — recalculado
       aqui porque o sabor/tamanho escolhido pode mudar o preço. */
    let smallest = null;
    rows.forEach((row) => {
      if (row.qty < 1) return;
      if (!smallest || row.qty < smallest.qty) smallest = row;
    });
    const reference = smallest ? smallest.variant.price / smallest.qty : 0;

    rows.forEach((row) => {
      this.paint(row.item, row.qty, row.variant, reference);
      if (row.variant.id === current.id) selectedItem = row.item;
    });

    if (selectedItem) this.markSelected(selectedItem);
    return selectedItem;
  }

  paint(item, qty, variant, reference) {
    const price = variant.price;
    const compare = variant.compare;
    const days = qty * this.config.daysPerUnit;

    const perDay = item.querySelector('[data-kit-perday]');
    if (perDay && days > 0) perDay.textContent = this.money(price / days);

    const unit = item.querySelector('[data-kit-unit]');
    if (unit) unit.textContent = this.money(qty > 1 ? price / qty : price);

    const total = item.querySelector('[data-kit-price]');
    if (total) total.textContent = this.money(price);

    /* O total aparece em dois lugares: na linha "À vista" e como apoio do
       número grande. Os dois precisam acompanhar a troca de variante. */
    item.querySelectorAll('[data-kit-total]').forEach((el) => {
      el.textContent = this.money(price);
    });

    const installment = item.querySelector('[data-kit-installment]');
    if (installment) {
      const parcels = this.installments(price);
      installment.textContent = 'ou ' + parcels.count + 'x de ' + this.money(parcels.value);
    }


    const save = item.querySelector('[data-kit-save]');
    if (save) {
      let savings = compare > price ? compare - price : 0;
      if (!savings && reference > 0 && qty > 0) {
        const referenceTotal = reference * qty;
        if (referenceTotal > price) savings = referenceTotal - price;
      }

      if (savings > 0) {
        const value = save.querySelector('[data-kit-save-value]');
        if (value) value.textContent = this.money(savings);
        save.hidden = false;
      } else {
        save.hidden = true;
      }
    }
  }
}

if (!customElements.get('kit-selector')) {
  customElements.define('kit-selector', KitSelector);
}
